import { Icon } from '../../icons';
import {
  Calendar as CalendarPrimitive,
  type CalendarClassNames,
  type CalendarProps as CalendarPrimitiveProps,
} from '../../primitives';
import { cn } from '../../shared';

export type { CalendarMatcher, CalendarModifiers, CalendarRange } from '../../primitives';

const DAY_SIZE = 'size-control-sm';

/** Every slot, because the library's defaults come from a stylesheet we do not import. */
const classNames: Partial<CalendarClassNames> = {
  root: 'text-body relative',
  // No breakpoint: a `sm:` rule would answer the viewport, which says nothing
  // about the room a portalled panel has.
  months: 'gap-group flex',
  month: 'gap-group flex flex-col',

  // Absolute so a two-month view gets one pair of arrows rather than one per month.
  nav: 'absolute inset-x-0 top-0 flex items-center justify-between',
  button_previous:
    'text-muted-foreground hover:text-foreground hover:bg-accent focus-ring rounded-md p-1 disabled:pointer-events-none disabled:opacity-40',
  button_next:
    'text-muted-foreground hover:text-foreground hover:bg-accent focus-ring rounded-md p-1 disabled:pointer-events-none disabled:opacity-40',
  chevron: 'block',

  month_caption: 'flex h-control-sm items-center justify-center',
  caption_label: 'text-title font-semibold',
  dropdowns: 'gap-inline flex items-center',
  dropdown: 'bg-field-bg border-input focus-ring rounded-md border px-1 text-body',
  dropdown_root: 'relative',

  month_grid: 'w-full border-collapse',
  weekdays: 'flex',
  weekday: cn(DAY_SIZE, 'text-caption text-muted-foreground font-normal'),
  weeks: '',
  week: 'flex w-full',

  week_number_header: cn(DAY_SIZE, 'text-caption text-muted-foreground font-normal'),
  week_number: cn(DAY_SIZE, 'text-caption text-muted-foreground'),

  day: cn(DAY_SIZE, 'relative p-0 text-center'),
  // The selection paint is on the cell and the button sits over it, so an
  // unscoped hover would wash a selected day out.
  day_button: cn(
    DAY_SIZE,
    'focus-ring relative rounded-md font-normal',
    'hover:bg-accent hover:text-accent-foreground',
    '[[data-selected]_&]:hover:bg-transparent [[data-selected]_&]:hover:text-inherit',
    'disabled:pointer-events-none',
  ),

  // A dot, not a ring: today is often inside a selected range, where a ring
  // collides with the band and the endpoint fill.
  today:
    'after:bg-current after:absolute after:bottom-0.5 after:left-1/2 after:size-1 after:-translate-x-1/2 after:rounded-full',
  outside: 'text-muted-foreground opacity-50',
  disabled: 'text-muted-foreground opacity-40',
  hidden: 'invisible',
};

const FILL = 'bg-primary text-primary-foreground';

const BAND = 'bg-accent text-accent-foreground';

/**
 * The library applies `selected` *and* a range slot to the same cell, and two
 * backgrounds at equal specificity race on emission order — so range mode leaves
 * `selected` empty rather than relying on one overriding the other.
 */
const SELECTION = {
  single: { selected: `${FILL} rounded-md` },
  multiple: { selected: `${FILL} rounded-md` },
  range: {
    selected: '',
    range_start: `${FILL} rounded-l-md`,
    range_middle: BAND,
    range_end: `${FILL} rounded-r-md`,
  },
} as const satisfies Record<'single' | 'multiple' | 'range', Partial<CalendarClassNames>>;

const CHEVRON_ICON = {
  left: 'chevron-left',
  right: 'chevron-right',
  up: 'chevron-up',
  down: 'chevron-down',
} as const;

/** A plain `Omit` over a union collapses the `mode` discriminant; distributing keeps each arm. */
type DistributiveOmit<T, K extends PropertyKey> = T extends unknown ? Omit<T, K> : never;

export type CalendarProps = DistributiveOmit<
  CalendarPrimitiveProps,
  'classNames' | 'components'
> & {
  classNames?: Partial<CalendarClassNames>;
};

/** A bare month grid. `DatePicker` composes the trigger, popover and field around it. */
export function Calendar({
  className,
  classNames: overrides,
  numberOfMonths = 1,
  // Off in a multi-month view: adjacent grids trail into each other, so the same
  // day would get a second button.
  showOutsideDays = numberOfMonths === 1,
  ...props
}: CalendarProps) {
  return (
    <CalendarPrimitive
      showOutsideDays={showOutsideDays}
      numberOfMonths={numberOfMonths}
      className={cn('w-fit', className)}
      classNames={{ ...classNames, ...SELECTION[props.mode ?? 'single'], ...overrides }}
      components={{
        Chevron: ({ orientation = 'left', className: chevronClass }) => (
          <Icon
            name={CHEVRON_ICON[orientation]}
            size="sm"
            aria-hidden="true"
            {...(chevronClass === undefined ? {} : { className: chevronClass })}
          />
        ),
      }}
      {...props}
    />
  );
}
